import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, X, User } from 'lucide-react';
import { atletasAPI } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';

const categorias = ['Adulto', 'Juvenil', 'Mirim', 'Comissão Técnica'];
const posicoes = ['Levantadora', 'Ponteira', 'Oposta', 'Central', 'Líbero', 'Técnica', 'Auxiliar'];

export default function AdminAtletas() {
  const [atletas, setAtletas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAtleta, setEditingAtleta] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    posicao: '',
    numero: '',
    foto_url: '',
    categoria: 'Adulto',
    bio: ''
  });
  const { toast } = useToast();

  const fetchAtletas = async () => {
    try {
      const res = await atletasAPI.listar();
      setAtletas(res.data);
    } catch (error) {
      console.error('Erro ao carregar atletas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAtletas();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        numero: formData.numero ? parseInt(formData.numero) : null
      };
      
      if (editingAtleta) {
        await atletasAPI.atualizar(editingAtleta.id, data);
        toast({ title: "Atleta atualizada com sucesso!" });
      } else {
        await atletasAPI.criar(data);
        toast({ title: "Atleta adicionada com sucesso!" });
      }
      
      setModalOpen(false);
      setEditingAtleta(null);
      setFormData({ nome: '', posicao: '', numero: '', foto_url: '', categoria: 'Adulto', bio: '' });
      fetchAtletas();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
  };

  const handleEdit = (atleta) => {
    setEditingAtleta(atleta);
    setFormData({
      nome: atleta.nome,
      posicao: atleta.posicao,
      numero: atleta.numero || '',
      foto_url: atleta.foto_url || '',
      categoria: atleta.categoria,
      bio: atleta.bio || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta atleta?')) return;
    try {
      await atletasAPI.deletar(id);
      toast({ title: "Atleta excluída com sucesso!" });
      fetchAtletas();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div data-testid="admin-atletas">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Atletas</h1>
          <p className="text-slate-600 mt-1">Gerencie as atletas do time</p>
        </div>
        <button
          onClick={() => {
            setEditingAtleta(null);
            setFormData({ nome: '', posicao: '', numero: '', foto_url: '', categoria: 'Adulto', bio: '' });
            setModalOpen(true);
          }}
          data-testid="add-atleta-btn"
          className="bg-blue-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 hover:bg-blue-800 transition-colors"
        >
          <Plus size={20} />
          Adicionar Atleta
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : atletas.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <User className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-bold text-slate-700 mb-2">Nenhuma atleta cadastrada</h3>
          <p className="text-slate-500">Clique em "Adicionar Atleta" para começar.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Atleta</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Posição</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Categoria</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Número</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {atletas.map((atleta) => (
                <tr key={atleta.id} className="hover:bg-slate-50" data-testid={`atleta-row-${atleta.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center overflow-hidden">
                        {atleta.foto_url ? (
                          <img src={atleta.foto_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${atleta.foto_url}` : atleta.foto_url} alt={atleta.nome} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                      <span className="font-medium text-slate-900">{atleta.nome}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{atleta.posicao}</td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                      {atleta.categoria}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{atleta.numero || '-'}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleEdit(atleta)}
                      data-testid={`edit-atleta-${atleta.id}`}
                      className="text-blue-600 hover:text-blue-800 p-2"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(atleta.id)}
                      data-testid={`delete-atleta-${atleta.id}`}
                      className="text-red-600 hover:text-red-800 p-2"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="atleta-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="font-heading text-xl font-bold">
                {editingAtleta ? 'Editar Atleta' : 'Nova Atleta'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Nome *</label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  data-testid="modal-input-nome"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  placeholder="Nome da atleta"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Posição *</label>
                  <select
                    name="posicao"
                    value={formData.posicao}
                    onChange={handleChange}
                    required
                    data-testid="modal-select-posicao"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="">Selecione</option>
                    {posicoes.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Categoria *</label>
                  <select
                    name="categoria"
                    value={formData.categoria}
                    onChange={handleChange}
                    required
                    data-testid="modal-select-categoria"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  >
                    {categorias.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Número</label>
                <input
                  type="number"
                  name="numero"
                  value={formData.numero}
                  onChange={handleChange}
                  data-testid="modal-input-numero"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  placeholder="Ex: 10"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">URL da Foto</label>
                <input
                  type="text"
                  name="foto_url"
                  value={formData.foto_url}
                  onChange={handleChange}
                  data-testid="modal-input-foto"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Biografia</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows={3}
                  data-testid="modal-input-bio"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100 resize-none"
                  placeholder="Breve descrição..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-lg font-bold text-slate-700 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="modal-submit-btn"
                  className="flex-1 px-6 py-3 bg-blue-700 text-white rounded-lg font-bold hover:bg-blue-800"
                >
                  {editingAtleta ? 'Salvar' : 'Adicionar'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
